/**
 * The client half of assistant-read water reports (PLAN-REMAINING C6).
 *
 * One POST, three shapes back: a profile ready to open as a draft, a reason
 * the paste was not enough, or an error that has nothing to do with the report
 * (no session, no allowance, no provider). The water page treats those as
 * three different sentences, which is why this module keeps them as three
 * different kinds instead of flattening everything into a message string.
 */

import { AI_BASE_PATH } from './protocol';

export const WATER_REPORT_PATH = `${AI_BASE_PATH}/water-report`;

/**
 * The paste's length bounds, shared with the route (`worker/ai/water-report`)
 * the way the AI protocol's constants are: the client refuses or trims before
 * asking, the server enforces regardless.
 */
export const WATER_REPORT_MIN_CHARS = 40;
export const WATER_REPORT_MAX_CHARS = 20_000;

/** Mirrors `worker/ai/water-report.ts#ExtractedWaterProfile`. */
export interface ExtractedWaterProfile {
  readonly name: string | null;
  readonly calciumPpm: number;
  readonly magnesiumPpm: number;
  /** Null where the report does not print it — not the same fact as 0. */
  readonly sodiumPpm: number | null;
  readonly chloridePpm: number | null;
  readonly sulfatePpm: number | null;
  readonly bicarbonatePpm: number;
  readonly ph: number | null;
  readonly alkalinityEntryForm: 'bicarbonate' | 'caco3';
}

/** One "< X" the report printed, resolved to zero — the notice names each. */
export interface BelowDetectionEntry {
  readonly ion: 'calcium' | 'magnesium' | 'sodium' | 'chloride' | 'sulfate' | 'alkalinity';
  readonly limitPpm: number;
}

/** What the report printed before the server's conversions, for the notice. */
export interface WaterReportPrinted {
  readonly sulfateAsSulfurPpm: number | null;
  readonly alkalinityAsCaco3Ppm: number | null;
  readonly belowDetection: readonly BelowDetectionEntry[];
  /** Flavour ions the report did not carry, in plain words, for the notice. */
  readonly notPrinted: readonly string[];
}

export type WaterReportResult =
  | {
      readonly kind: 'sufficient';
      readonly profile: ExtractedWaterProfile;
      readonly printed: WaterReportPrinted;
    }
  | { readonly kind: 'insufficient'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

const FAILED = 'We could not reach the assistant. Try again in a moment.';

/** Send the paste; never throws — a network failure is an `error` result. */
export async function extractWaterReport(text: string): Promise<WaterReportResult> {
  let response: Response;
  try {
    response = await fetch(WATER_REPORT_PATH, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text }),
    });
  } catch {
    return { kind: 'error', message: FAILED };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'error', message: FAILED };
  }
  if (typeof body !== 'object' || body === null) return { kind: 'error', message: FAILED };
  const parsed = body as {
    ok?: boolean;
    sufficient?: boolean;
    profile?: ExtractedWaterProfile;
    printed?: WaterReportPrinted;
    reason?: string;
    message?: string;
  };

  if (!response.ok || parsed.ok !== true) {
    // 400 / 429 / 503 all carry a sentence written for the user.
    return { kind: 'error', message: typeof parsed.message === 'string' ? parsed.message : FAILED };
  }
  if (parsed.sufficient === true && parsed.profile !== undefined && parsed.printed !== undefined) {
    return { kind: 'sufficient', profile: parsed.profile, printed: parsed.printed };
  }
  if (parsed.sufficient === false && typeof parsed.reason === 'string') {
    return { kind: 'insufficient', reason: parsed.reason };
  }
  return { kind: 'error', message: FAILED };
}
